/**
 * Prompts Tab — Admin Client Detail
 * CRUD for PromptTemplate entities (list/detail/create views).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Plus } from "@untitledui/icons";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { Select, type SelectItemType } from "@/components/base/select/select";
import { SelectItem } from "@/components/base/select/select-item";
import { TextArea } from "@/components/base/textarea/textarea";
import { Toggle } from "@/components/base/toggle/toggle";
import { useAdminApi } from "@/services/api/admin-api";
import type {
    CreatePromptTemplateRequestDto,
    DocumentTypeAdminListItemDto,
    PromptTemplateAdminDetailDto,
    PromptTemplateAdminListItemDto,
    StepTypeAdminListItemDto,
    UpdatePromptTemplateRequestDto,
} from "@/types/api/admin";

// =============================================================================
// TYPES
// =============================================================================

interface PromptsTabProps {
    clientId: number;
}

type ViewMode = "list" | "detail" | "create";

// =============================================================================
// PROMPT LIST SECTION
// =============================================================================

function PromptListSection({
    clientId,
    onSelectPrompt,
    onCreatePrompt,
}: {
    clientId: number;
    onSelectPrompt: (id: number) => void;
    onCreatePrompt: () => void;
}) {
    const adminApi = useAdminApi();

    const [prompts, setPrompts] = useState<PromptTemplateAdminListItemDto[]>([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [skip, setSkip] = useState(0);
    const pageSize = 50;

    const fetchPrompts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await adminApi.listPromptTemplates(clientId, pageSize, skip);
            setPrompts(res.data);
            setTotal(res.total);
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load prompt templates.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId, skip]);

    useEffect(() => {
        fetchPrompts();
    }, [fetchPrompts]);

    return (
        <div>
            {/* Header */}
            <div className="mb-4 flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-primary">Prompt Templates</h3>
                    <p className="text-sm text-tertiary">
                        Manage LLM prompt templates for this client. System defaults are shown but cannot be edited — create a client-specific override instead.
                    </p>
                </div>
                <Button color="primary" size="sm" iconLeading={Plus} onClick={onCreatePrompt}>
                    Create Prompt
                </Button>
            </div>

            {/* Error */}
            {error && <p className="mb-3 text-sm text-error-primary">{error}</p>}

            {/* Loading */}
            {isLoading ? (
                <div className="mt-4">
                    <LoadingIndicator size="sm" label="Loading prompts..." />
                </div>
            ) : prompts.length === 0 ? (
                <p className="py-8 text-center text-sm text-quaternary">No prompt templates found.</p>
            ) : (
                <>
                    {/* Table */}
                    <div className="overflow-x-auto rounded-lg border border-secondary">
                        <table className="w-full text-left text-sm">
                            <thead className="border-b border-secondary bg-secondary_subtle">
                                <tr>
                                    <th className="px-4 py-3 font-medium text-secondary">Name</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Step Type</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Document Type</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Model</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Scope</th>
                                    <th className="px-4 py-3 font-medium text-secondary">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary">
                                {prompts.map((p) => (
                                    <tr key={p.id} className="cursor-pointer hover:bg-primary_hover" onClick={() => onSelectPrompt(p.id)}>
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-primary">{p.name}</div>
                                            {p.description && <div className="mt-0.5 text-xs text-tertiary">{p.description}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-tertiary">{p.stepTypeName ?? "—"}</td>
                                        <td className="px-4 py-3 text-tertiary">{p.documentTypeName ?? "—"}</td>
                                        <td className="px-4 py-3">
                                            {p.modelHint ? (
                                                <span className="font-mono text-xs text-tertiary">{p.modelHint}</span>
                                            ) : (
                                                <span className="text-quaternary">—</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge color={p.clientId != null ? "brand" : "gray"} size="sm" type="pill-color">
                                                {p.clientId != null ? "Client" : "System"}
                                            </Badge>
                                        </td>
                                        <td className="px-4 py-3">
                                            <Badge color={p.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                                {p.isActive ? "Active" : "Inactive"}
                                            </Badge>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {total > pageSize && (
                        <div className="mt-3 flex items-center justify-between text-sm text-tertiary">
                            <span>
                                Showing {skip + 1}–{Math.min(skip + pageSize, total)} of {total}
                            </span>
                            <div className="flex gap-2">
                                <Button color="secondary" size="sm" isDisabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - pageSize))}>
                                    Previous
                                </Button>
                                <Button color="secondary" size="sm" isDisabled={skip + pageSize >= total} onClick={() => setSkip(skip + pageSize)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// =============================================================================
// PROMPT DETAIL FORM
// =============================================================================

function PromptDetailForm({
    clientId,
    promptId,
    isCreate,
    onBack,
    onSaved,
}: {
    clientId: number;
    promptId?: number;
    isCreate: boolean;
    onBack: () => void;
    onSaved: () => void;
}) {
    const adminApi = useAdminApi();

    // Reference data
    const [stepTypes, setStepTypes] = useState<StepTypeAdminListItemDto[]>([]);
    const [documentTypes, setDocumentTypes] = useState<DocumentTypeAdminListItemDto[]>([]);

    // Detail state
    const [detail, setDetail] = useState<PromptTemplateAdminDetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(!isCreate);
    const [error, setError] = useState<string | null>(null);

    // Saving state
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Form fields
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [stepTypeId, setStepTypeId] = useState<string | null>(null);
    const [documentTypeId, setDocumentTypeId] = useState<string | null>(null);
    const [isActive, setIsActive] = useState(true);
    const [modelHint, setModelHint] = useState("");
    const [temperature, setTemperature] = useState("");
    const [maxTokens, setMaxTokens] = useState("");
    const [promptText, setPromptText] = useState("");
    const [systemPrompt, setSystemPrompt] = useState("");
    const [outputSchema, setOutputSchema] = useState("");

    const isSystemPromptTemplate = detail != null && detail.clientId == null;

    // Step type dropdown options
    const stepTypeOptions: SelectItemType[] = useMemo(
        () => [{ id: "", label: "— None —" }, ...stepTypes.map((st) => ({ id: String(st.id), label: st.name }))],
        [stepTypes],
    );

    // Document type dropdown options
    const documentTypeOptions: SelectItemType[] = useMemo(
        () => [{ id: "", label: "— None —" }, ...documentTypes.map((dt) => ({ id: String(dt.id), label: dt.name }))],
        [documentTypes],
    );

    // Load reference data
    useEffect(() => {
        let cancelled = false;
        async function loadRefs() {
            try {
                const [st, dt] = await Promise.all([adminApi.listStepTypes(), adminApi.listDocumentTypes()]);
                if (!cancelled) {
                    setStepTypes(st);
                    setDocumentTypes(dt);
                }
            } catch {
                // reference data load failure is non-fatal
            }
        }
        loadRefs();
        return () => {
            cancelled = true;
        };
    }, [adminApi]);

    // Load detail (edit mode only)
    useEffect(() => {
        if (isCreate || !promptId) return;
        let cancelled = false;
        async function loadDetail() {
            setIsLoading(true);
            setError(null);
            try {
                const d = await adminApi.getPromptTemplate(clientId, promptId!);
                if (cancelled) return;
                setDetail(d);
                setName(d.name);
                setDescription(d.description ?? "");
                setStepTypeId(d.stepTypeId != null ? String(d.stepTypeId) : null);
                setDocumentTypeId(d.documentTypeId != null ? String(d.documentTypeId) : null);
                setIsActive(d.isActive);
                setModelHint(d.modelHint ?? "");
                setTemperature(d.temperature != null ? String(d.temperature) : "");
                setMaxTokens(d.maxTokens != null ? String(d.maxTokens) : "");
                setPromptText(d.promptText);
                setSystemPrompt(d.systemPrompt ?? "");
                setOutputSchema(d.outputSchema ?? "");
            } catch (err) {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : "Failed to load prompt template.");
                }
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        }
        loadDetail();
        return () => {
            cancelled = true;
        };
    }, [adminApi, clientId, promptId, isCreate]);

    // Save handler
    const handleSave = useCallback(async () => {
        setIsSaving(true);
        setSaveError(null);
        setSaveSuccess(false);

        // Validate outputSchema is valid JSON if provided
        if (outputSchema.trim()) {
            try {
                JSON.parse(outputSchema);
            } catch {
                setSaveError("Output Schema must be valid JSON.");
                setIsSaving(false);
                return;
            }
        }

        try {
            if (isCreate) {
                const dto: CreatePromptTemplateRequestDto = {
                    name: name.trim(),
                    description: description.trim() || null,
                    stepTypeId: stepTypeId ? Number(stepTypeId) : null,
                    documentTypeId: documentTypeId ? Number(documentTypeId) : null,
                    promptText: promptText,
                    systemPrompt: systemPrompt.trim() || null,
                    outputSchema: outputSchema.trim() || null,
                    modelHint: modelHint.trim() || null,
                    temperature: temperature ? Number(temperature) : null,
                    maxTokens: maxTokens ? Number(maxTokens) : null,
                    isActive,
                };
                await adminApi.createPromptTemplate(clientId, dto);
                onSaved();
                onBack();
            } else {
                const dto: UpdatePromptTemplateRequestDto = {
                    name: name.trim(),
                    description: description.trim() || null,
                    stepTypeId: stepTypeId ? Number(stepTypeId) : null,
                    documentTypeId: documentTypeId ? Number(documentTypeId) : null,
                    promptText: promptText,
                    systemPrompt: systemPrompt.trim() || null,
                    outputSchema: outputSchema.trim() || null,
                    modelHint: modelHint.trim() || null,
                    temperature: temperature ? Number(temperature) : null,
                    maxTokens: maxTokens ? Number(maxTokens) : null,
                    isActive,
                };
                const updated = await adminApi.updatePromptTemplate(clientId, promptId!, dto);
                setDetail(updated);
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (err) {
            setSaveError(err instanceof Error ? err.message : "Failed to save prompt template.");
        } finally {
            setIsSaving(false);
        }
    }, [
        adminApi,
        clientId,
        promptId,
        isCreate,
        name,
        description,
        stepTypeId,
        documentTypeId,
        isActive,
        modelHint,
        temperature,
        maxTokens,
        promptText,
        systemPrompt,
        outputSchema,
        onSaved,
        onBack,
    ]);

    if (isLoading) {
        return <LoadingIndicator size="sm" label="Loading prompt template..." />;
    }

    if (error) {
        return (
            <div>
                <Button color="secondary" size="sm" iconLeading={ArrowLeft} onClick={onBack}>
                    Back to list
                </Button>
                <p className="mt-3 text-sm text-error-primary">{error}</p>
            </div>
        );
    }

    return (
        <div>
            {/* Back button */}
            <Button color="secondary" size="sm" iconLeading={ArrowLeft} onClick={onBack}>
                Back to list
            </Button>

            <h3 className="mt-4 text-lg font-semibold text-primary">{isCreate ? "Create Prompt Template" : `Edit: ${detail?.name ?? "Prompt"}`}</h3>

            {/* System default warning */}
            {isSystemPromptTemplate && (
                <div className="border-warning bg-warning_subtle mt-3 rounded-lg border px-4 py-3 text-sm text-warning-primary">
                    This is a system default prompt and cannot be edited via the client endpoint. Create a client-specific override instead.
                </div>
            )}

            {/* Form */}
            <div className="mt-6 max-w-2xl space-y-8">
                {/* Core fields */}
                <FormSection title="Core">
                    <div className="grid grid-cols-2 gap-4">
                        <Input label="Name" placeholder="e.g. Invoice Parser" value={name} onChange={setName} isRequired isDisabled={isSystemPromptTemplate} />
                        <Toggle
                            label="Active"
                            hint="Only active prompts are used during processing"
                            isSelected={isActive}
                            onChange={setIsActive}
                            isDisabled={isSystemPromptTemplate}
                            size="sm"
                        />
                    </div>
                    <div className="mt-4">
                        <TextArea
                            label="Description"
                            placeholder="What this prompt does..."
                            value={description}
                            onChange={setDescription}
                            rows={2}
                            isDisabled={isSystemPromptTemplate}
                        />
                    </div>
                </FormSection>

                {/* Targeting */}
                <FormSection title="Targeting">
                    <div className="grid grid-cols-2 gap-4">
                        <Select
                            label="Step Type"
                            items={stepTypeOptions}
                            selectedKey={stepTypeId ?? ""}
                            onSelectionChange={(key) => setStepTypeId(key === "" ? null : String(key))}
                            isDisabled={isSystemPromptTemplate}
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                        <Select
                            label="Document Type"
                            items={documentTypeOptions}
                            selectedKey={documentTypeId ?? ""}
                            onSelectionChange={(key) => setDocumentTypeId(key === "" ? null : String(key))}
                            isDisabled={isSystemPromptTemplate}
                            size="sm"
                        >
                            {(item) => <SelectItem id={item.id}>{item.label}</SelectItem>}
                        </Select>
                    </div>
                </FormSection>

                {/* Model Configuration */}
                <FormSection title="Model Configuration">
                    <div className="grid grid-cols-3 gap-4">
                        <Input
                            label="Model Hint"
                            placeholder="e.g. claude-3-opus"
                            value={modelHint}
                            onChange={setModelHint}
                            isDisabled={isSystemPromptTemplate}
                        />
                        <Input label="Temperature" placeholder="0.0 – 2.0" value={temperature} onChange={setTemperature} isDisabled={isSystemPromptTemplate} />
                        <Input label="Max Tokens" placeholder="e.g. 4096" value={maxTokens} onChange={setMaxTokens} isDisabled={isSystemPromptTemplate} />
                    </div>
                </FormSection>

                {/* Prompt Content */}
                <FormSection title="Prompt Content">
                    <div className="space-y-4">
                        <TextArea
                            label="Prompt Text"
                            placeholder="The main prompt sent to the LLM..."
                            value={promptText}
                            onChange={setPromptText}
                            rows={12}
                            isRequired
                            isDisabled={isSystemPromptTemplate}
                            textAreaClassName="font-mono text-xs"
                        />
                        <TextArea
                            label="System Prompt"
                            placeholder="Optional system-level instruction..."
                            value={systemPrompt}
                            onChange={setSystemPrompt}
                            rows={6}
                            isDisabled={isSystemPromptTemplate}
                            textAreaClassName="font-mono text-xs"
                        />
                        <TextArea
                            label="Output Schema"
                            hint="JSON schema for structured output (must be valid JSON)"
                            placeholder='{ "type": "object", "properties": { ... } }'
                            value={outputSchema}
                            onChange={setOutputSchema}
                            rows={8}
                            isDisabled={isSystemPromptTemplate}
                            textAreaClassName="font-mono text-xs"
                        />
                    </div>
                </FormSection>

                {/* Save button */}
                {!isSystemPromptTemplate && (
                    <div className="flex items-center gap-3">
                        <Button color="primary" size="sm" onClick={handleSave} isDisabled={isSaving || !name.trim() || !promptText.trim()}>
                            {isSaving ? "Saving..." : isCreate ? "Create Prompt" : "Save Changes"}
                        </Button>
                        <Button color="secondary" size="sm" onClick={onBack}>
                            Cancel
                        </Button>
                        {saveSuccess && <span className="text-sm text-success-primary">Saved successfully.</span>}
                        {saveError && <span className="text-sm text-error-primary">{saveError}</span>}
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// FORM SECTION HELPER
// =============================================================================

function FormSection({ title, children }: { title: string; children: React.ReactNode }) {
    return (
        <div>
            <h4 className="mb-3 border-b border-secondary pb-2 text-sm font-semibold text-secondary">{title}</h4>
            {children}
        </div>
    );
}

// =============================================================================
// PROMPTS TAB (EXPORT)
// =============================================================================

export function PromptsTab({ clientId }: PromptsTabProps) {
    const [viewMode, setViewMode] = useState<ViewMode>("list");
    const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
    const [listKey, setListKey] = useState(0);

    const handleSelectPrompt = useCallback((id: number) => {
        setSelectedPromptId(id);
        setViewMode("detail");
    }, []);

    const handleCreatePrompt = useCallback(() => {
        setSelectedPromptId(null);
        setViewMode("create");
    }, []);

    const handleBack = useCallback(() => {
        setViewMode("list");
        setSelectedPromptId(null);
    }, []);

    const handleSaved = useCallback(() => {
        setListKey((k) => k + 1);
    }, []);

    if (viewMode === "detail" && selectedPromptId != null) {
        return <PromptDetailForm clientId={clientId} promptId={selectedPromptId} isCreate={false} onBack={handleBack} onSaved={handleSaved} />;
    }

    if (viewMode === "create") {
        return <PromptDetailForm clientId={clientId} isCreate onBack={handleBack} onSaved={handleSaved} />;
    }

    return <PromptListSection key={listKey} clientId={clientId} onSelectPrompt={handleSelectPrompt} onCreatePrompt={handleCreatePrompt} />;
}
